import { Icon, type IconName } from '@/components/primitives';
import type { AuditAction, AuditLog } from '@/domain';

interface Props {
  entries: AuditLog[];
  title?: string;
  emptyText?: string;
}

const ACTION_ICON: Partial<Record<AuditAction, IconName>> = {
  CREATE: 'plus',
  UPDATE: 'edit',
  DELETE: 'trash',
  IMPORT: 'upload',
  SUBMIT: 'spark',
  STATE_TRANSITION: 'arrowRight',
  FORECAST_RUN: 'forecast',
  CONFIG_CHANGE: 'sliders',
  LOGIN: 'user',
  LOGOUT: 'logout',
};

const ACTION_TONE: Partial<Record<AuditAction, { bg: string; fg: string }>> = {
  CREATE: { bg: '#eef2ff', fg: '#0b43ea' },
  UPDATE: { bg: '#fefce8', fg: '#92400e' },
  DELETE: { bg: '#fef2f2', fg: '#b91c1c' },
  IMPORT: { bg: '#fefce8', fg: '#92400e' },
  SUBMIT: { bg: '#eef2ff', fg: '#0b43ea' },
  STATE_TRANSITION: { bg: '#f0fdf4', fg: '#15803d' },
  FORECAST_RUN: { bg: '#eff6ff', fg: '#1e40af' },
  CONFIG_CHANGE: { bg: '#eef2ff', fg: '#0b43ea' },
  LOGIN: { bg: 'var(--bx-surface-2)', fg: 'var(--bx-text-muted)' },
  LOGOUT: { bg: 'var(--bx-surface-2)', fg: 'var(--bx-text-muted)' },
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AuditTimeline({
  entries,
  title = 'Nhật ký',
  emptyText = 'Chưa có hoạt động',
}: Props) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--bx-border-muted)',
        borderRadius: 'var(--bx-radius-lg)',
        boxShadow: 'var(--bx-shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--bx-border-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: 'var(--bx-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
        }}
      >
        <Icon name="audit" size={11} />
        <span>{title}</span>
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--bx-font-mono)',
            color: 'var(--bx-text-muted)',
          }}
        >
          {entries.length}
        </span>
      </div>
      <div style={{ padding: 14, overflowY: 'auto', maxHeight: 480 }}>
        {entries.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--bx-text-muted)',
              fontSize: 12.5,
            }}
          >
            {emptyText}
          </div>
        ) : (
          entries.map((e, i) => {
            const icon = ACTION_ICON[e.action] ?? 'info';
            const tone = ACTION_TONE[e.action] ?? {
              bg: 'var(--bx-surface-2)',
              fg: 'var(--bx-text-muted)',
            };
            return (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  paddingBottom: 14,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 8,
                      background: tone.bg,
                      color: tone.fg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={icon} size={11} />
                  </div>
                  {i < entries.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        width: 1,
                        background: 'var(--bx-border-muted)',
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: 'var(--bx-text-dark)',
                      lineHeight: 1.45,
                    }}
                  >
                    {e.summary}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--bx-text-muted)',
                      marginTop: 4,
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontFamily: 'var(--bx-font-mono)' }}>
                      {fmtTime(e.created_at)}
                    </span>
                    <span>·</span>
                    <span>{e.actor}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
